import { Component, OnDestroy, OnInit, Inject } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { DOCUMENT } from '@angular/common';
import { Observable, Subscription } from 'rxjs';
import { ModularProfileConfig, ProfileState } from '../../../redux/models/account/profile.state';
import { IProfileConfig, Option } from '../../../redux/models/account/IProfile.model';
import { getProfileInfo, __config_profile } from '../../../redux/reducers';
import * as ProfileAction from '../../../redux/actions/account/actions-profile';
import { customerConfig } from '@uxdf/direct-shared-lib';
import { filter, skipWhile, take } from 'rxjs/operators';
import { PageScrollService } from 'ngx-page-scroll-core';
import { State } from '../../../redux/reducers/customer';

// localized strings
@Component({
  standalone: false,
  selector: 'app-profile-page',
  templateUrl: './profile-page.component.html',
  styleUrls: ['./profile-page.component.scss']
})
export class ProfilePageComponent implements OnInit, OnDestroy {
  profileFormDecisionMakerString = $localize`:@@profile-page.profileFormDecisionMaker:You are an administrator:`;
  public customerConfig$: Observable<State>;
  public profileForm: UntypedFormGroup;
  public profileState$: Observable<ProfileState>;
  public profileConfig: IProfileConfig;
  public userTitles: Option[] = [];
  public jobPositions: Option[] = [];
  public jobAreas: Option[] = [];
  public primaryBusinesses: Option[] = [];
  public preferredLanguages: Option[] = [];
  public timezones: Option[] = [];
  public formSubscr: Subscription;
  public showValidation = false;
  public noTitlePlaceholder = '';
  public noTimeZonePlaceholder = '';
  public storeID = 0;
  readonly profilePhoneNumberPlaceholder = $localize`:@@profile-page.profilePhoneNumberPlaceholder:+(country code) phone number`;

  private readonly onlyLatinChar = '^[A-Za-zÀ-ÖØ-öø-ÿ.\\s-]{0,50}$'; //new regEx:Latin letters,"-"," ","." and latin letters with diacritics
  private readonly emailPattern =
    '(([^<>()\\[\\]\\.,;:\\s@\\"]+(\\.[^<>()\\[\\]\\.,;:\\s@\\"]+)*)' +
    '|(\\".+\\"))@(([^<>()[\\]\\.,;:\\s@\\"]+\\.)+[^<>()[\\]\\.,;:\\s@\\"]{2,})$';
  private readonly phoneNumberPattern = '[0-9\\s()+-]+';

  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    private readonly store: Store<ProfileState>,
    private readonly formBuilder: FormBuilder,
    private readonly pageScrollService: PageScrollService
  ) {}

  ngOnInit() {
    this.customerConfig$ = this.store.pipe(select(customerConfig));
    this.store
      .pipe(
        select(__config_profile),
        skipWhile(profileConfig => !profileConfig.config),
        take(1)
      )
      .subscribe((moduleProfileConfig: ModularProfileConfig) => {
        this.profileConfig = moduleProfileConfig.config;
        this.store.dispatch(new ProfileAction.GetProfile());
        this.profileState$ = this.store.pipe(select(getProfileInfo));
        this.formSubscr = this.getFormSubscription();
        this.processPlaceholders();
      });
  }

  ngOnDestroy() {
    this.formSubscr.unsubscribe();
  }

  onSubmit() {
    if (!this.profileForm.invalid) {
      // to force re-setting the store based on the new backend data: PTR 23909485
      if (this.storeID !== this.profileForm.value.store_id) {
        sessionStorage.setItem('preferredLocale', null);
      }
      this.store.dispatch(new ProfileAction.UpdateProfileInit(this.profileForm.value));

      return;
    }
    this.showValidation = true;
    this.profileForm.markAllAsTouched();
    const firstInvalidElementId = document.getElementById('formUserEdit').querySelector('.ng-invalid').id;
    this.pageScrollService.scroll({
      document: this.document,
      scrollTarget: '#' + firstInvalidElementId,
      scrollOffset: 200,
      duration: 500
    });
  }

  processPlaceholders() {
    if (this.profileForm !== undefined) {
      const labelnothingSelectedString = $localize`:@@profile-page.nothingSelected:Nothing selected`;
      this.noTitlePlaceholder = +this.profileForm.value.name_title_option === 0 ? labelnothingSelectedString : '';
      this.noTimeZonePlaceholder = +this.profileForm.value.timezone === 0 ? labelnothingSelectedString : '';
    }
  }

  private getSelectedValue(data: { value?: number; values?: Option[] }, required = false) {
    if (!data) {
      return 0;
    }
    if (!data.values) {
      return data.value || 0;
    }
    let selected = data.values.find(({ value }) => value === data.value);
    if (!selected && required) {
      selected = data.values[0];
    }
    return selected ? selected.value : 0;
  }

  private getFormSubscription() {
    return this.profileState$.pipe(filter(profile => !profile.isLoading)).subscribe((profile: ProfileState) => {
      this.userTitles = [];
      this.jobPositions = profile.job_position.values || [];
      this.jobAreas = profile.job_area.values || [];
      this.primaryBusinesses = profile.primary_business_option.values || [];
      this.preferredLanguages = profile.store_id.values || [];
      this.timezones = profile.timezone.values || [];
      this.storeID = profile.store_id.value;

      const controls: any = {
        firstname: [profile.firstname, [Validators.required, Validators.pattern(this.onlyLatinChar)]],
        lastname: [profile.lastname, [Validators.required, Validators.pattern(this.onlyLatinChar)]],
        firstname_local: [profile.firstname_local, []],
        lastname_local: [profile.lastname_local, []],
        contactEmail: [
           { value: profile.contactEmail, disabled: false },
          [Validators.required, Validators.pattern(this.emailPattern)]
        ],
        timezone: [this.getSelectedValue(profile.timezone, true), [Validators.required]],
        name_title_option: [typeof profile.nameTitleOption === 'string' ? profile.nameTitleOption : this.getSelectedValue(profile.nameTitleOption), [Validators.required]],
        job_area: [this.getSelectedValue(profile.job_area), []],
        job_position: [this.getSelectedValue(profile.job_position), []],
        primary_business_option: [this.getSelectedValue(profile.primary_business_option, true), [Validators.required]],
        store_id: [this.getSelectedValue(profile.store_id), [Validators.required]],
        work_phone_number: [
          profile.workPhoneNumber,
          [Validators.required, Validators.pattern(this.phoneNumberPattern)]
        ],
        mobile_phone_number: [profile.mobilePhoneNumber, Validators.pattern(this.phoneNumberPattern)],
        street: [{ value: profile.address.street, disabled: true }, []],
        city: [{ value: profile.address.city, disabled: true }, []],
        postal_code: [{ value: profile.address.postalCode, disabled: true }, []],
        country: [{ value: profile.address.country, disabled: true }, []],
        office_id: [{ value: profile.address.officeId, disabled: true }, []],
        province: [{ value: profile.address.province, disabled: true }, []],
        marketing_email_allowed: [profile.marketing_email_allowed]
      };

      this.profileForm = this.formBuilder.group(controls, {});
    });
  }
}
